import { useState, useEffect } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import {CartesianGrid, XAxis, YAxis, Area, AreaChart } from 'recharts';
import { conLog } from '../utils/conLog';
import { ArrowDownIcon, ArrowUpIcon, Clock, ExpandIcon, MessageCircle, MessagesSquare, Users } from "lucide-react";
import AimdocLoader from '../ui/AimdocLoader';

// shadcn imports
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { type ChartConfig } from "@/components/ui/chart"
import { ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { InteractiveBarChart } from "./InteractiveBarChart"
import LeadsPerDay from './LeadsPerDay';
import VisitorsPerDay from './VisitorsPerDay';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";


interface AnalyticsData {
    total_messages_last_7_days: number;
    total_conversations_last_7_days: number;
    messages_per_day: {
        day: string;
        message_count: number;
    }[];
    conversations_per_day: {
        day: string;
        conversation_count: number;
    }[];
    visitors_per_day: {
        day: string;
        visitor_count: number;
    }[];
    messages_by_hour: {
        hour: number;
        message_count: number;
    }[];
    average_conversation_duration_minutes: number;
    previous_week: {
        total_messages: number;
        total_conversations: number;
        average_messages_per_conversation: number;
        average_conversation_duration_minutes: number;
    };
    combined_data: {
        day: string;
        message_count: number;
        conversation_count: number;
    }[];
    time_range?: string;
}

const chartConfig = {
    messages: {
        label: "Messages",
        color: "hsl(210, 80%, 70%)"  // Light blue
    },
    conversations: {
        label: "Conversations",
        color: "hsl(210, 80%, 40%)"  // Dark blue
    }
} satisfies ChartConfig

type MetricCard = {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    getValue: (data: AnalyticsData) => number;
    getPreviousValue: (data: AnalyticsData) => number;
    suffix?: string;
};

const getMetricCards = (timeRange: string) => {
    const timeRangeLabel = timeRangeOptions.find(opt => opt.value === timeRange)?.label.toLowerCase() || 'period';
    
    return [
        {
            title: "Total Messages",
            icon: MessageCircle,
            getValue: (data: AnalyticsData) => data.total_messages_last_7_days,
            getPreviousValue: (data: AnalyticsData) => data.previous_week?.total_messages ?? 0,
        },
        {
            title: "Total Conversations",
            icon: Users,
            getValue: (data: AnalyticsData) => data.total_conversations_last_7_days,
            getPreviousValue: (data: AnalyticsData) => data.previous_week?.total_conversations ?? 0,
        },
        {
            title: "Avg Messages/Conversation",
            icon: MessagesSquare,
            getValue: (data: AnalyticsData) => data.total_conversations_last_7_days 
                ? (data.total_messages_last_7_days / data.total_conversations_last_7_days)
                : 0,
            getPreviousValue: (data: AnalyticsData) => data.previous_week?.average_messages_per_conversation ?? 0,
        },
        {
            title: "Avg Duration",
            icon: Clock,
            getValue: (data: AnalyticsData) => data.average_conversation_duration_minutes,
            getPreviousValue: (data: AnalyticsData) => data.previous_week?.average_conversation_duration_minutes ?? 0,
            suffix: "min"
        },
    ];
};

const TrendIndicator = ({ currentValue, previousValue }: { currentValue: number, previousValue: number }) => {
    // If both values are 0, show "No data"
    if (currentValue === 0 && previousValue === 0) {
        return (
            <div className="hidden lg:block text-xs text-muted-foreground">
                No data available
            </div>
        );
    }
    
    // If previousValue is 0 but currentValue isn't, show it's new data
    if (previousValue === 0 && currentValue > 0) {
        return (
            <div className="hidden lg:block text-xs text-yellow-500 font-medium">
                New data
            </div>
        );
    }

    // If currentValue is 0 but previousValue isn't, show "100% down"
    if (currentValue === 0 && previousValue > 0) {
        return (
            <div className="hidden lg:flex flex-col items-end">
                <div className="flex items-center gap-1 text-xs font-medium text-red-500">
                    <ArrowDownIcon className="w-3 h-3" />
                    <span>100%</span>
                </div>
                <span className="text-[10px] text-muted-foreground">
                    vs previous period
                </span>
            </div>
        );
    }
    
    // Simple percentage change calculation
    let percentageChange = 0;
    if (previousValue !== 0) {
        percentageChange = ((currentValue - previousValue) / previousValue) * 100;
    }
    
    const isPositive = percentageChange > 0;
    
    if (Math.abs(percentageChange) < 0.1) {
        return (
            <div className="hidden lg:block text-xs text-muted-foreground">
                No change
            </div>
        );
    }
    
    return (
        <div className="hidden lg:flex flex-col items-end">
            <div className={`flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {isPositive ? <ArrowUpIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
                <span>{Math.abs(percentageChange).toFixed(1)}%</span>
            </div>
            <span className="text-[10px] text-muted-foreground">
                vs previous period
            </span>
        </div>
    );
};

// Define time range options
const timeRangeOptions = [
    { value: "24h", label: "Last 24 Hours" },
    { value: "7d", label: "Last Week" },
    { value: "30d", label: "Last Month" },
    { value: "90d", label: "Last Quarter" },
    { value: "365d", label: "Last Year" }
];

const Analytics = () => {
    const { get } = useApi();
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [timeRange, setTimeRange] = useState("7d"); // Default to Last Week

    useEffect(() => {
        fetchAnalyticsData();
    }, [timeRange]); // Re-fetch when time range changes

    const fetchAnalyticsData = async () => {
        try {
            const response = await get(`/analytics?time_range=${timeRange}`);
            conLog(response.data);
            
            // Create combined data for messages and conversations
            const combinedData = response.data.messages_per_day.map((msgData: { day: any; message_count: any; }) => {
                const convData = response.data.conversations_per_day.find(
                    (conv: { day: any; }) => conv.day === msgData.day
                );
                return {
                    day: msgData.day,
                    message_count: msgData.message_count,
                    conversation_count: convData?.conversation_count || 0
                };
            }).sort((a: { day: string | number | Date; }, b: { day: string | number | Date; }) => new Date(a.day).getTime() - new Date(b.day).getTime());

            const sortedData = {
                ...response.data,
                conversations_per_day: [...response.data.conversations_per_day].sort(
                    (a: { day: string | number | Date; }, b: { day: string | number | Date; }) => new Date(a.day).getTime() - new Date(b.day).getTime()
                ),
                combined_data: combinedData,
                time_range: timeRange
            };
            setAnalyticsData(sortedData);
        } catch (error) {
            toast.error('Error fetching analytics data');
        }
    };

    if (!analyticsData) {
        return <AimdocLoader label="Loading analytics" />;
    }

    // Format date based on time range
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        if (timeRange === "24h") {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (timeRange === "7d") {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else if (timeRange === "30d") {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    // Generate appropriate X-axis tick count based on time range
    const getTickCount = () => {
        switch (timeRange) {
            case "24h": return 6; // Every 4 hours
            case "7d": return 7; // Every day
            case "30d": return 10; // Every 3 days
            case "90d": return 12; // Weekly
            case "365d": return 12; // Monthly
            default: return 7;
        }
    };

    const metricCards = getMetricCards(timeRange);
    
    return (
        <div className="grow">
            {/* Time Range Selector */}
            <div className="mb-3 flex justify-end">
                <Select value={timeRange} onValueChange={setTimeRange}>
                    <SelectTrigger className="h-9 w-[160px]">
                        <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                        {timeRangeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                {metricCards.map((card, index) => (
                    <Card key={index} className="rounded-lg">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                            <CardTitle className="text-sm font-medium">
                                {card.title}
                            </CardTitle>
                            <card.icon className="w-4 h-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent className="px-4 pb-4">
                            <div className="flex flex-row items-baseline justify-between">
                                <div className="text-xl font-semibold">
                                    {card.getValue(analyticsData).toFixed(1)}
                                    {card.suffix && <span className="text-sm ml-1">{card.suffix}</span>}
                                </div>
                                <TrendIndicator 
                                    currentValue={card.getValue(analyticsData)}
                                    previousValue={card.getPreviousValue(analyticsData)}
                                />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">

                            </p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Combined Area Chart */}
            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {/* Combined Area Chart */}
                <Card className="rounded-lg">
                    <CardHeader className="flex items-center gap-2 space-y-0 border-b p-4 sm:flex-row">
                        <div className="grid flex-1 gap-1 text-center sm:text-left">
                            <CardTitle className="text-sm font-semibold">Messages & conversations</CardTitle>
                            <CardDescription className="text-xs">
                                Combined view of {timeRangeOptions.find(opt => opt.value === timeRange)?.label.toLowerCase()}
                            </CardDescription>
                        </div>
                    </CardHeader>
                    <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                        {analyticsData.messages_per_day.length === 0 ? (
                            <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                                No data yet!
                            </div>
                        ) : (
                            <ChartContainer
                                config={chartConfig}
                                className="aspect-auto h-[250px] w-full"
                            >
                                <AreaChart data={analyticsData.combined_data}>
                                    <defs>
                                        <linearGradient id="fillMessages" x1="0" y1="0" x2="0" y2="1">
                                            <stop
                                                offset="5%"
                                                stopColor="var(--color-messages)"
                                                stopOpacity={0.8}
                                            />
                                            <stop
                                                offset="95%"
                                                stopColor="var(--color-messages)"
                                                stopOpacity={0.1}
                                            />
                                        </linearGradient>
                                        <linearGradient id="fillConversations" x1="0" y1="0" x2="0" y2="1">
                                            <stop
                                                offset="5%"
                                                stopColor="var(--color-conversations)"
                                                stopOpacity={0.8}
                                            />
                                            <stop
                                                offset="95%"
                                                stopColor="var(--color-conversations)"
                                                stopOpacity={0.1}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="day"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        minTickGap={32}
                                        tickFormatter={formatDate}
                                        tickCount={getTickCount()}
                                    />
                                    <YAxis tickLine={false} axisLine={false} />
                                    <ChartTooltip
                                        cursor={false}
                                        content={
                                            <ChartTooltipContent
                                                labelFormatter={(value) => formatDate(value)}
                                                indicator="dot"
                                            />
                                        }
                                    />
                                    <Area
                                        dataKey="message_count"
                                        type="monotone"
                                        fill="url(#fillMessages)"
                                        stroke="var(--color-messages)"
                                        name="messages"
                                        stackId="1"
                                    />
                                    <Area
                                        dataKey="conversation_count"
                                        type="monotone"
                                        fill="url(#fillConversations)"
                                        stroke="var(--color-conversations)"
                                        name="conversations"
                                        stackId="2"
                                    />
                                    <ChartLegend 
                                        content={<ChartLegendContent nameKey="name" />}
                                        verticalAlign="bottom"
                                        align="center"
                                    />
                                </AreaChart>
                            </ChartContainer>
                        )}
                        <Button 
                            variant="outline" 
                            className="mt-4 w-full"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            Expand <ExpandIcon className="w-4 h-4 ml-2" />
                        </Button>
                    </CardContent>
                </Card>

                {/* Leads Per Day Chart */}
                <Card className="rounded-lg">
                <CardHeader className="flex items-center gap-2 space-y-0 border-b p-4 sm:flex-row">
                    <div className="grid flex-1 gap-1 text-center sm:text-left">
                        <CardTitle className="text-sm font-semibold">Contacts</CardTitle>
                        <CardDescription className="text-xs">
                            {timeRangeOptions.find(opt => opt.value === timeRange)?.label.toLowerCase()} contact activity
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    <LeadsPerDay timeRange={timeRange} />
                </CardContent>
                </Card>
            </div>

            <Card className="mt-3 rounded-lg">
                <CardHeader className="flex items-center gap-2 space-y-0 border-b p-4 sm:flex-row">
                    <div className="grid flex-1 gap-1 text-center sm:text-left">
                        <CardTitle className="text-sm font-semibold">Visitors</CardTitle>
                        <CardDescription className="text-xs">
                            {timeRangeOptions.find(opt => opt.value === timeRange)?.label.toLowerCase()} new visitor activity
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    <VisitorsPerDay
                        visitorsPerDay={analyticsData.visitors_per_day || []}
                        timeRange={timeRange}
                    />
                </CardContent>
            </Card>

        {/* Activity Overview Drawer */}
        <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <SheetContent side="bottom" className="h-[60vh] w-full">
                <SheetHeader className="mb-4">
                    <SheetTitle>Browse by Activity Type</SheetTitle>
                </SheetHeader>
                {/* Replace the two cards with InteractiveBarChart */}
                <div className="mt-4">
                    <InteractiveBarChart data={analyticsData.combined_data} />
                </div>
            </SheetContent>
        </Sheet>
        </div>
    );
};

export default Analytics;